/**
 * CRM Multi-Compagnia · Lista Clienti + form CRUD + drawer dettaglio
 */
import { useState } from "react";
import { Users, Plus, Mail, Phone, Edit3, Trash2 } from "lucide-react";
import {
  api, useApi, useDebounced, DataTable, Spinner, ErrorBox, Btn, Drawer,
  Field, TextInput, Select, StatusBadge, toast, fmtDate, RamoChip
} from "@/components/crm-multi/shared";

const EMPTY = {
  anagrafica: { tipo: "persona_fisica", nome: "", cognome: "", ragione_sociale: "", codice_fiscale: "", partita_iva: "", email: "", telefono: "" },
  stato_cliente: "potenziale", note_interne: "", tags: [],
};

export default function CrmMultiClientiPage() {
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const dq = useDebounced(q);
  const list = useApi(() => api.get("/customers", { q: dq || undefined, stato: stato || undefined, limit: 100 }), [dq, stato]);
  const [drawerId, setDrawerId] = useState(null);
  const [editing, setEditing] = useState(null);
  const items = list.data?.items || [];
  const selected = items.find(c => c.id === drawerId);

  const onSave = async (form) => {
    try {
      const payload = JSON.parse(JSON.stringify(form));
      // sanitize anagrafica: drop empty optional strings → backend rifiuta EmailStr vuoto
      ["email", "pec"].forEach(k => { if (payload.anagrafica?.[k] === "") delete payload.anagrafica[k]; });
      if (editing.mode === "new") await api.post("/customers", payload);
      else await api.patch(`/customers/${editing.data.id}`, payload);
      toast("Cliente salvato", "success"); setEditing(null); list.refresh();
    } catch (e) { toast(JSON.stringify(e?.response?.data?.detail || e.message), "error"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare il cliente?")) return;
    try { await api.del(`/customers/${id}`); toast("Eliminato", "success"); list.refresh(); setDrawerId(null); }
    catch (e) { toast(e.message, "error"); }
  };

  return (
    <div className="space-y-4" data-testid="cm-clienti-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2"><Users className="w-5 h-5" /> Clienti</h2>
          <p className="text-sm text-zinc-500">{list.data?.total ?? 0} clienti totali</p>
        </div>
        <Btn variant="primary" onClick={() => setEditing({ mode: "new", data: EMPTY })} data-testid="cm-clienti-new"><Plus className="w-4 h-4" /> Nuovo cliente</Btn>
      </header>

      <div className="grid md:grid-cols-2 gap-2">
        <TextInput placeholder="Cerca nome, CF, P.IVA, email, telefono..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="cm-clienti-search" />
        <Select value={stato} onChange={(e) => setStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="attivo">Attivo</option><option value="potenziale">Potenziale</option>
          <option value="perso">Perso</option><option value="ex">Ex cliente</option>
        </Select>
      </div>

      <ErrorBox error={list.error} />
      {list.loading ? <Spinner /> : (
        <DataTable
          testid="cm-clienti-table"
          onRowClick={(r) => setDrawerId(r.id)}
          columns={[
            { key: "anag", label: "Nome / Ragione sociale", render: r => {
              const a = r.anagrafica || {};
              return <div><p className="font-medium">{a.tipo === "azienda" ? a.ragione_sociale : `${a.nome || ""} ${a.cognome || ""}`}</p>
                <p className="text-[10px] text-zinc-400 uppercase">{a.tipo === "azienda" ? "Azienda" : "Privato"}</p></div>;
            }},
            { key: "cf_piva", label: "CF / P.IVA", render: r => <span className="font-mono text-[11px]">{r.anagrafica?.codice_fiscale || r.anagrafica?.partita_iva || "—"}</span> },
            { key: "email", label: "Contatti", render: r => (
              <div className="text-xs space-y-0.5">
                {r.anagrafica?.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-zinc-400" />{r.anagrafica.email}</div>}
                {r.anagrafica?.telefono && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-zinc-400" />{r.anagrafica.telefono}</div>}
              </div>
            )},
            { key: "stato", label: "Stato", render: r => <StatusBadge tipo="generic" valore={r.stato_cliente} /> },
            { key: "subagent_id", label: "Sub.", render: r => <span className="text-[10px] font-mono text-zinc-500">{r.subagent_id ? r.subagent_id.slice(0, 6) : "—"}</span> },
          ]}
          rows={items}
          emptyTitle="Nessun cliente"
          emptyHint="Aggiungi il primo cliente o esegui il seed demo."
        />
      )}

      <Drawer
        open={!!selected} onClose={() => setDrawerId(null)}
        title={selected?.anagrafica?.tipo === "azienda" ? selected.anagrafica.ragione_sociale : `${selected?.anagrafica?.nome || ""} ${selected?.anagrafica?.cognome || ""}`}
        subtitle={`Stato: ${selected?.stato_cliente} · Creato il ${fmtDate(selected?.created_at)}`}
        actions={selected && (
          <>
            <Btn size="sm" variant="outline" onClick={() => setEditing({ mode: "edit", data: selected })}><Edit3 className="w-3.5 h-3.5" /></Btn>
            <Btn size="sm" variant="danger" onClick={() => onDelete(selected.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
          </>
        )}
        testid="cm-clienti-drawer"
      >
        {selected && <ClienteDetailTabs customer={selected} />}
      </Drawer>

      {editing && <ClienteEditModal data={editing.data} mode={editing.mode} onSave={onSave} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ClienteDetailTabs({ customer }) {
  const [tab, setTab] = useState("dati");
  const polizze = useApi(() => api.get("/policies", { customer_id: customer.id }), [customer.id]);
  const attivita = useApi(() => api.get("/activities", { customer_id: customer.id, limit: 20 }), [customer.id]);

  return (
    <div>
      <nav className="flex gap-1 border-b border-zinc-200 mb-4">
        {[["dati","Anagrafica"],["polizze",`Polizze (${polizze.data?.total ?? "..."})`],["attivita",`Attività (${attivita.data?.total ?? "..."})`]].map(([id,l]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500"}`}>{l}</button>
        ))}
      </nav>
      {tab === "dati" && <pre className="text-xs bg-zinc-50 p-3 rounded overflow-auto">{JSON.stringify(customer.anagrafica, null, 2)}</pre>}
      {tab === "polizze" && (polizze.data?.items || []).map(p => (
        <div key={p.id} className="border rounded p-2 mb-2 text-sm flex items-center justify-between">
          <div><span className="font-mono text-xs">{p.numero_polizza}</span> <RamoChip ramo={p.ramo} /></div>
          <div className="text-xs"><span className="text-zinc-500">scad. {fmtDate(p.data_scadenza)} ·</span> <StatusBadge tipo="polizza" valore={p.stato} /></div>
        </div>
      ))}
      {tab === "polizze" && (polizze.data?.items || []).length === 0 && <p className="text-sm text-zinc-500">Nessuna polizza.</p>}
      {tab === "attivita" && (attivita.data?.items || []).length === 0 && <p className="text-sm text-zinc-500">Nessuna attività.</p>}
      {tab === "attivita" && (attivita.data?.items || []).map(a => (
        <div key={a.id} className="border-l-2 border-emerald-500 pl-2 mb-2 text-sm">
          <p className="font-medium">{a.titolo}</p><p className="text-xs text-zinc-500">{a.tipo} · {fmtDate(a.data_pianificata || a.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

function ClienteEditModal({ data, mode, onSave, onClose }) {
  const [form, setForm] = useState(data);
  const a = form.anagrafica || {};
  const ua = (k, v) => setForm({ ...form, anagrafica: { ...form.anagrafica, [k]: v } });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cm-cliente-edit">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b flex justify-between"><h3 className="text-sm font-semibold">{mode === "new" ? "Nuovo cliente" : "Modifica cliente"}</h3><button onClick={onClose} className="text-xl">×</button></header>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-5 space-y-3">
          <Field label="Tipo cliente" required>
            <Select value={a.tipo || "persona_fisica"} onChange={(e) => ua("tipo", e.target.value)}>
              <option value="persona_fisica">Persona fisica</option><option value="azienda">Azienda</option>
            </Select>
          </Field>
          {a.tipo === "azienda" ? (
            <>
              <Field label="Ragione sociale" required><TextInput required value={a.ragione_sociale || ""} onChange={(e) => ua("ragione_sociale", e.target.value)} /></Field>
              <Field label="Partita IVA" required><TextInput required value={a.partita_iva || ""} onChange={(e) => ua("partita_iva", e.target.value)} /></Field>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" required><TextInput required value={a.nome || ""} onChange={(e) => ua("nome", e.target.value)} /></Field>
              <Field label="Cognome" required><TextInput required value={a.cognome || ""} onChange={(e) => ua("cognome", e.target.value)} /></Field>
              <Field label="Codice fiscale"><TextInput value={a.codice_fiscale || ""} onChange={(e) => ua("codice_fiscale", e.target.value.toUpperCase())} /></Field>
              <Field label="Data nascita"><TextInput type="date" value={a.data_nascita || ""} onChange={(e) => ua("data_nascita", e.target.value)} /></Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><TextInput type="email" value={a.email || ""} onChange={(e) => ua("email", e.target.value)} /></Field>
            <Field label="Telefono"><TextInput value={a.telefono || ""} onChange={(e) => ua("telefono", e.target.value)} /></Field>
          </div>
          <Field label="Stato cliente">
            <Select value={form.stato_cliente || "potenziale"} onChange={(e) => setForm({ ...form, stato_cliente: e.target.value })}>
              <option value="potenziale">Potenziale</option><option value="attivo">Attivo</option>
              <option value="perso">Perso</option><option value="ex">Ex cliente</option>
            </Select>
          </Field>
          <Field label="Note interne"><textarea className="w-full px-3 py-1.5 text-sm border border-zinc-300 rounded" rows={2} value={form.note_interne || ""} onChange={(e) => setForm({ ...form, note_interne: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-3 border-t"><Btn variant="outline" type="button" onClick={onClose}>Annulla</Btn><Btn variant="primary" type="submit" data-testid="cm-cliente-form-save">Salva</Btn></div>
        </form>
      </div>
    </div>
  );
}
